/** @jsxRuntime classic */
/** @jsx React.createElement */
/** @jsxFrag React.Fragment */
const React = require('react');
const { Helmet } = require('react-helmet-async');
const { useLocation } = require('react-router-dom');
const {
  getPublicPageSeo,
  isNoindexPath,
  isPageManagedMetadataPath,
} = require('../site/publicPageSeo');
const { ogImageUrlForPath } = require('../site/ogTitleCard');
const { SITE_BASE_URL } = require('../site/siteRoutes');
const { structuredDataForRoute } = require('../site/structuredData');

function dynamicMetadata(pathname) {
  if (pathname.startsWith('/blog/')) {
    return {
      path: pathname,
      title: 'BotSpot Blog Article',
      description: 'Market analysis, AI trading insights, and product updates from BotSpot.',
    };
  }
  if (pathname.startsWith('/marketplace/strategy/')) {
    return {
      path: pathname,
      title: 'AI Trading Strategy | BotSpot Marketplace',
      description: 'Review strategy details, backtest results, and live trading history in the BotSpot marketplace.',
    };
  }
  return null;
}

function PublicRouteSeo() {
  const { pathname } = useLocation();
  const normalizedPathname = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
  if (
    isNoindexPath(normalizedPathname)
    || isPageManagedMetadataPath(normalizedPathname)
    || normalizedPathname.startsWith('/blog/')
    || normalizedPathname.startsWith('/marketplace/strategy/')
  ) return null;
  const routeSeo = getPublicPageSeo(normalizedPathname);
  const metadata = routeSeo || dynamicMetadata(normalizedPathname);
  if (!metadata) return null;
  // The static route document's JSON-LD is removed on mount with the other
  // data-static-seo tags, so React must publish it again or Google, which
  // indexes the rendered DOM, never sees it (2026-09-23 crawler audit).
  const structuredData = routeSeo ? structuredDataForRoute(routeSeo) : [];

  const canonical = `${SITE_BASE_URL}${normalizedPathname}`;
  const image = metadata.image || ogImageUrlForPath(normalizedPathname);
  return (
    <Helmet>
      <title>{metadata.title}</title>
      <meta name="description" content={metadata.description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={metadata.title} />
      <meta property="og:description" content={metadata.description} />
      <meta property="og:image" content={image} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metadata.title} />
      <meta name="twitter:description" content={metadata.description} />
      <meta name="twitter:image" content={image} />
      {structuredData.map((item, index) => (
        <script type="application/ld+json" key={`public-route-jsonld-${index}`}>
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
}

module.exports = PublicRouteSeo;
